/**
 * Curated HPO body-system roots used by the Phenotypes section's
 * category browser and "Group by body system" toggle. Mirrors the
 * default category set on enterprise PhenoTips.
 *
 * Per PTV2-1051 #1, this list is hardcoded for Phase 1; an admin
 * surface to configure visibility + order is a follow-up.
 *
 * Each entry's `id` is the HPO root term id. The picker uses
 * `subtreeRootId` to scope search to its descendants; the row
 * grouper checks `feature.hpoId` against `feature`'s `term_category`
 * via the mapper-surfaced category — fallback: "Other".
 */
export interface HpoCategory {
  /** HPO root id; descendants of this term belong to the category. */
  readonly id: string;
  /** i18n key suffix under `patient.phenotypes.categories.*`. */
  readonly key: string;
}

export const HPO_CATEGORIES: readonly HpoCategory[] = [
  { id: 'HP:0001507', key: 'growth' },
  { id: 'HP:0000152', key: 'headAndNeck' },
  { id: 'HP:0000478', key: 'eye' },
  { id: 'HP:0000598', key: 'ear' },
  { id: 'HP:0001626', key: 'cardiovascular' },
  { id: 'HP:0002086', key: 'respiratory' },
  { id: 'HP:0025031', key: 'digestive' },
  { id: 'HP:0033127', key: 'musculoskeletal' },
  { id: 'HP:0001574', key: 'integument' },
  { id: 'HP:0000707', key: 'nervousSystem' },
  { id: 'HP:0000708', key: 'behavioral' },
];

/** Sentinel returned by {@link categorizeFeature} when no root matches. */
export const HPO_CATEGORY_OTHER = '__other__';

/**
 * Return the first {@link HPO_CATEGORIES} entry whose root id appears
 * in the feature's `term_category` ancestor set, or
 * {@link HPO_CATEGORY_OTHER} when the feature is uncategorised (free
 * text, off-catalogue, or under a root we don't surface).
 */
export function categorizeFeature(
  feature: { readonly hpoId: string; readonly termCategory?: readonly string[] | null },
): string {
  const cats = feature.termCategory ?? [];
  for (const c of HPO_CATEGORIES) {
    if (feature.hpoId === c.id) return c.id;
    if (cats.includes(c.id)) return c.id;
  }
  return HPO_CATEGORY_OTHER;
}
